/**
 * The last few failed saves, loads and server calls, kept so the screen that shows one can offer "Report" for it.
 * The write wrappers and the `/api` caller note each failure here; a surface looks one up by the exact sentence it
 * is showing. A sentence nothing noted (a field check, a pre-flight) finds nothing, so it carries no Report.
 * This module imports nothing: the write path notes into it and the reporter reads from it.
 */
export type FailureReason = 'refused' | 'stale' | 'signed-out' | 'forbidden' | 'rejected' | 'invalid' | 'conflict'
  | 'offline' | 'network' | 'server' | 'crash' | 'load';

export interface Failure {
  reason: FailureReason;
  /** The sentence the person is shown. */
  message: string;
  /** What a report carries in place of `message`, when the sentence shown names something the person chose (a file). */
  reportAs?: string;
  code?: string;
  details?: string;
  apiPath?: string;
  apiStatus?: number;
  traceId?: string;
  at: number;
}

const KEEP = 5;
/** Newest first. `shown` holds the other sentences a surface showed for the same failure. */
const ring: Array<{ failure: Failure; shown: string[] }> = [];

let lastAt = 0;
export function noteFailure(f: Omit<Failure, 'at'>): void {
  // no two failures share a moment: `at` also tells a control whether the failure beside it is still the same one
  lastAt = Math.max(Date.now(), lastAt + 1);
  ring.unshift({ failure: { ...f, at: lastAt }, shown: [] });
  if (ring.length > KEEP) ring.length = KEEP;
}

/** The surface reworded the message: the newest failure noted under `original` is also found under `shown`. */
export function aliasFailure(original: string, shown: string, reportAs?: string): void {
  if (original === shown) return;
  const hit = ring.find((e) => e.failure.message === original || e.shown.includes(original));
  if (!hit) return;
  if (!hit.shown.includes(shown)) hit.shown.push(shown);
  // the sentence shown names a person: the report carries this one instead
  if (reportAs) hit.failure = { ...hit.failure, reportAs };
}

/** The newest failure shown as exactly this sentence. A reworded one comes back under the sentence that was shown. */
export function findFailure(message: string): Failure | null {
  for (const e of ring) {
    if (e.failure.message === message) return e.failure;
    if (e.shown.includes(message)) return { ...e.failure, message, details: e.failure.details ?? e.failure.reportAs ?? e.failure.message };
  }
  return null;
}

/** Nothing to fix and nowhere for a click to land: offline, a dropped connection, a signed-out session. */
export function isReportable(f: Failure | null): f is Failure {
  return f !== null && f.reason !== 'offline' && f.reason !== 'network' && f.reason !== 'signed-out';
}

/**
 * A surface that shows a sentence in place keeps hold of the failure it found: the control stays while the sentence
 * does, however many newer failures push it out of the list. A newer failure under the same sentence takes its place;
 * a different sentence starts again.
 */
export function pinFailure(pinned: Failure | null, message: string | null | undefined): Failure | null {
  if (!message) return null;
  const found = findFailure(message);
  if (found) return pinned && pinned.at === found.at && pinned.message === found.message ? pinned : found;
  return pinned && pinned.message === message ? pinned : null;
}

const reasonOfStatus = (status: number): FailureReason =>
  status === 401 ? 'signed-out' : status === 403 ? 'forbidden' : status === 409 ? 'conflict' : status === 400 || status === 422 ? 'invalid' : status >= 500 ? 'server' : 'rejected';
const DROPPED = /Failed to fetch|NetworkError|fetch failed|Load failed|timed? ?out|AbortError/i;
const httpStatus = (v: unknown): number | undefined => {
  const n = typeof v === 'string' && /^[0-9]{3}$/.test(v) ? Number(v) : v;
  return typeof n === 'number' && n >= 100 && n <= 599 ? n : undefined;
};

/**
 * A picture or file that storage did not take, shown as `message`. The report names the bucket, never the file.
 * `reportAs` is for a sentence that names the file the person chose.
 */
export function noteStorageFailure(bucket: string, error: unknown, message: string, reportAs?: string): void {
  const e = (error ?? {}) as { message?: string; status?: unknown; statusCode?: unknown };
  const text = typeof e.message === 'string' ? e.message : '';
  const apiPath = `/storage/v1/object/${bucket}`;
  if ((typeof navigator !== 'undefined' && navigator.onLine === false) || DROPPED.test(text)) return noteFailure({ reason: 'network', message, reportAs, apiPath });
  // storage answers some refusals with 400 and the real status inside the answer
  const status = httpStatus(e.statusCode) ?? httpStatus(e.status);
  noteFailure({ reason: status ? reasonOfStatus(status) : 'server', message, reportAs, details: text || undefined, apiPath, apiStatus: status });
}

/**
 * An answer from the sign-in service, or from one of this app's own doors a person uses before signing in. Only the
 * service failing is noted (5xx): a wrong password, a wrong confirmation number, a used link are answers, not failures. Returns
 * whether it was noted, so the screen can say the service is down rather than blame what was typed.
 */
export function noteServiceFailure(apiPath: string, status: number | null | undefined, message: string, extra: { details?: string; traceId?: string } = {}): boolean {
  if (typeof status !== 'number' || status < 500 || status > 599) return false;
  noteFailure({ reason: 'server', message, apiPath, apiStatus: status, details: extra.details, traceId: extra.traceId });
  return true;
}

/** A signed-in call to this app's own `/api` made outside the shared caller, answered with a refusal or a failure. */
export function noteApiFailure(apiPath: string, status: number, message: string, extra: { details?: string; traceId?: string } = {}): void {
  noteFailure({ reason: reasonOfStatus(status), message, apiPath, apiStatus: httpStatus(status), details: extra.details, traceId: extra.traceId });
}

export function resetFailures(): void {
  ring.length = 0;
}
